import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DropshippedProductPriceManagementTableItem from './DropshippedProductPriceManagementTableItem';
import useNotification from '../../../../../components/_hook/useNotification';
import ButtonBatch from '../../../../../components/_button/ButtonBatch';
import GraphQLTable from '../../../../../components/_table/GraphQLTable';
import {
  IProductQueryData,
  IProductVars,
  useProductQuery,
} from '../../../../../../graphql/query/product/useProductQuery';
import { IProductSchema } from '../../../../../../graphql/type/product';
import Context from '../../../../../context';
import ProductDropshippedPriceManagementBatchUpdate from './_module/ProductDropshippedPriceManagementBatchUpdate';
import { useBatchUpdatePriceDropshippedProductMutation } from '../../../../../../graphql/mutation/product/batchUpdatePriceDropshippedProductMutation';

interface IProps {
  dropshippedCompanyId?: string;
}

export default function DropshippedProductPriceManagementTable(props: IProps) {
  interface IItem extends IProductSchema {
    key: string;
    variationId?: string;
    priceCurrency?: string;
    priceFinal?: number;
    variation?: IProductSchema['variations'][0];
  }

  const { dropshippedCompanyId } = props;
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { company } = useContext(Context);
  const [isEditing, setIsEditing] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<IItem[]>([]);
  const [isShowDialogBatch, setIsShowDialogBatch] = useState(false);
  const [itemsChanged, setItemsChanged] = useState<IItem[]>([]);

  const [
    batchUpdatePriceDropshippedProductMutation,
    { loading: isBatchUpdatingPriceDropshippedProduct },
  ] = useBatchUpdatePriceDropshippedProductMutation(
    `updatedProducts {
				_id
				prices {
					final
					currency
				}
				variations { 
                  id
                  attributes
                }
			}`,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.batchUpdatePriceDropshippedProduct &&
          data.batchUpdatePriceDropshippedProduct.updatedProducts
        ) {
          notification.success(
            t('__item___updated', {
              item: `${
                data.batchUpdatePriceDropshippedProduct.updatedProducts.length
              } ${t('product', {
                count:
                  data.batchUpdatePriceDropshippedProduct.updatedProducts
                    .length,
              })}`,
            })
          );
          setTableRefreshKey(new Date().getTime());
        }
        setIsEditing(false);
        setItemsChanged([]);
      },
      onError: (e) => {
        setIsEditing(false);
        notification.error(e.message);
      },
    }
  );

  async function onClickSave() {
    await batchUpdatePriceDropshippedProductMutation({
      variables: {
        batchUpdatePriceDropshippedProducts: itemsChanged.map((item) => ({
          productId: item._id,
          variationId: item.variationId,
          priceCurrency: item.priceCurrency,
          priceFinal: item.priceFinal,
        })),
      },
    });
  }

  return (
    <>
      <Dialog
        open={isShowDialogBatch}
        maxWidth="md"
        fullWidth
        onClose={() => setIsShowDialogBatch(false)}
      >
        <ProductDropshippedPriceManagementBatchUpdate
          batchUpdatePriceDropshippedProducts={selectedItems.map(
            (selectedItem) => ({
              productId: selectedItem._id,
              variationId: selectedItem.variationId,
            })
          )}
          onCompleted={() => {
            setSelectedItems([]);
            setIsShowDialogBatch(false);
          }}
          onCancel={() => setIsShowDialogBatch(false)}
        />
      </Dialog>
      <Grid container spacing={1}>
        {isEditing ? (
          <Grid item>
            <Button
              disabled={isBatchUpdatingPriceDropshippedProduct}
              variant="contained"
              color="primary"
              onClick={onClickSave}
              startIcon={<SaveIcon />}
            >
              {t('save')}
            </Button>
          </Grid>
        ) : (
          <>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setSelectedItems([]);
                  setIsEditing(true);
                }}
              >
                {t('edit')}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <GraphQLTable<IProductQueryData, IItem, IProductVars>
        useCheckbox={!isEditing}
        actionButtons={[
          <ButtonBatch
            key="batch"
            color="primary"
            variant="contained"
            size="small"
            text={`${t('batch')} ${t('update')} ${t('price')}`}
            onClick={() => setIsShowDialogBatch(true)}
          />,
        ]}
        refreshKey={tableRefreshKey}
        select={`
				_id
				ordering
				company
				companyData {
				    _id
					origin
					slug
					aka {
                        la
                        text
                    }
					name {
                        la
                        text
                    }
				}
				name {
					la
					text
				}
				slug
				status 
				featured 
				prices {
					final
					currency
				}
				variations { 
                  id
                  attributes
                }
                sourceDropshippedProductID
                sourceDropshippedProductData {
                  _id
                  prices {
                      final
                      currency
                  }
                  variations { 
                    id
                    attributes
                    dropshipping {
                      prices {
                        currency
                        final
                      }
                    }
                  }
                  dropshipping {
                      prices {
                        currency
                        final
                      }
                  }
                }
                sourceDropshippedProductCompanyID
                sourceDropshippedProductCompanyData {
                    _id
					origin
					slug
					aka {
                        la
                        text
                    }
                    verification
					name {
                        la
                        text
                    }
                }
                dropshippingProductRequestID
                dropshippingProductRequestData {
                  _id
                  customPrices {
                    currency
                    final
                    variations {
                      variationID
                      prices {
                        currency
                        final
                      }
                    }
                  }
                }
       	 `}
        useQuery={useProductQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'name',
            label: t('name'),
            align: 'left',
          },
          {
            field: 'company',
            label: t('company'),
            align: 'center',
          },
          {
            field: 'sales_price_for_dropshipper',
            label: t('sales_price_for_dropshipper'),
            align: 'center',
          },
          {
            field: 'custom_displayed_sales_price',
            label: t('custom_displayed_sales_price'),
            align: 'center',
          },
        ]}
        renderItem={(item) => (
          <DropshippedProductPriceManagementTableItem
            item={item}
            setTableRefreshKey={setTableRefreshKey}
            tableRefreshKey={tableRefreshKey}
            isEditable={isEditing}
            onChange={(tempItem) => {
              let isExists = false;
              const tempItemsChanged = itemsChanged.map((itemChanged) => {
                if (
                  tempItem._id === itemChanged._id &&
                  tempItem.variationId === itemChanged.variationId
                ) {
                  isExists = true;
                  return tempItem;
                }

                return itemChanged;
              });

              if (!isExists) {
                tempItemsChanged.push(tempItem);
              }

              setItemsChanged(tempItemsChanged);
            }}
          />
        )}
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        defaultFilterOptions={{
          companyId: company._id,
          isDropshippedProduct: true,
          sourceDropshippedProductCompanyId: dropshippedCompanyId,
        }}
        searchFields={{
          _id: { label: t('id') },
          name: { label: t('name') },
        }}
        title={t('price_management')}
        defaultSort="createdTime"
        defaultSortOrder={-1}
        itemKey="key"
        filterData={(data) => {
          const dataWithVariations: IItem[] = [];

          data.forEach((item) => {
            dataWithVariations.push({
              ...item,
              ...{
                key: item._id,
                priceCurrency:
                  item.prices && item.prices.currency
                    ? item.prices.currency
                    : '',
                priceFinal:
                  item.prices && item.prices.final ? item.prices.final : 0,
              },
            });

            if (item.variations && item.variations.length) {
              item.variations.forEach((variation) => {
                dataWithVariations.push({
                  ...item,
                  ...{
                    variation,
                    key: `${item._id}$$${variation.id}`,
                    variationId: variation.id,
                    priceFinal:
                      variation.attributes && variation.attributes.prices
                        ? variation.attributes.prices
                        : 0,
                  },
                });
              });
            }
          });

          return dataWithVariations;
        }}
      />
    </>
  );
}
